import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { postRequest, getRequest } from '../../utils/axios';
import { NotificationManager } from 'react-notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faTrash } from '@fortawesome/free-solid-svg-icons';

import MainA from '../../layout/Main Admin';
import Table from '../../components/atoms/Table';
import Button from '../../components/atoms/Button';
import Upload from '../../components/atoms/Upload';
import Modal from '../../components/atoms/Modal';
import Loader from '../../components/atoms/Loader';
import Input from '../../components/atoms/Input';

import plus from '../../assets/icons/plus.svg';
import backArrow from '../../assets/icons/arrow-left.svg';
import sampleFile from '../../assets/file/sample-file.csv';
import upload from '../../assets/icons/upload_image.svg';

import './styles.scss';

const Participants = () => {
    const [participantData, setParticipantData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [clientId, setClientId] = useState(null);
    const [pageId, setPageId] = useState(null);
    const [pageName, setPageName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [sortColumn, setSortColumn] = useState('bibNo');
    const [sortOrder, setSortOrder] = useState('asc');
    const [csvData, setCSVData] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const pageIdFromQuery = queryParams.get('pageId');
        const clientIdFromQuery = queryParams.get('clientId');
        const pageNameFromQuery = queryParams.get('pageName');

        setPageId(Number(pageIdFromQuery));
        setClientId(Number(clientIdFromQuery));
        setPageName(pageNameFromQuery || '');
        if (pageIdFromQuery) {
            fetchParticipants(pageIdFromQuery);
        }
    }, [location.search]);

    const handleSort = (column) => {
        const order = sortColumn === column && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortColumn(column);
        setSortOrder(order);

        const sortedData = [...filteredData].sort((a, b) => {
            const aValue = a[column] || '';
            const bValue = b[column] || '';

            if (aValue < bValue) return order === 'asc' ? -1 : 1;
            if (aValue > bValue) return order === 'asc' ? 1 : -1;
            return 0;
        });

        setFilteredData(sortedData);
    };


    const fetchParticipants = async (pageId) => {
        setLoading(true);
        try {
            const response = await getRequest(`EventParticipant/GetEventParticipants/${pageId}`);
            const data = response.data || [];
            setParticipantData(data);
            setFilteredData(data);
        } catch (error) {
            NotificationManager.error('Failed to fetch participants.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const parsedData = parseCSV(e.target.result);
                setCSVData(parsedData);
            } catch (error) {
                NotificationManager.error('Invalid CSV file format.');
                console.error(error);
            }
        };

        reader.onerror = () => {
            NotificationManager.error('Failed to read the file.');
        };

        reader.readAsText(file);
    };

    const parseCSV = (data) => {
        const rows = data.split('\n').filter(row => row.trim() !== '');

        if (rows.length === 0) return [];

        // Detect delimiter: comma or tab
        const delimiter = rows[0].includes('\t') ? '\t' : ',';

        const headers = rows[0]
            .split(delimiter)
            .map(header => header.trim().replace(/^\ufeff/, ''));

        return rows.slice(1).map(row => {
            const values = row.split(delimiter).map(value => value.trim());

            return headers.reduce((acc, header, index) => {
                const value = values[index] || '';

                if (header.toLowerCase() === 'bib number') acc.bibNo = value;
                if (header.toLowerCase() === 'ticket name') acc.ticketName = value;
                if (header.toLowerCase() === 'attendee name') acc.participantName = value;
                if (header.toLowerCase() === 'completion time') acc.completionTime = value;
                if (header.toLowerCase() === 'email') acc.email = value;
                if (header.toLowerCase() === 'phone number') acc.phoneNumber = value;
                if (header.toLowerCase() === 'age category') acc.ageCategory = value;
                if (header.toLowerCase() === 'overall rank') acc.overallRank = value;
                if (header.toLowerCase() === 'gender rank') acc.genderRank = value;
                if (header.toLowerCase() === 'age rank') acc.ageRank = value;
                if (header.toLowerCase() === 'run type') acc.runType = value;

                return acc;
            }, {});
        });
    };


    const uploadCSVData = async (csvData) => {
        const apiData = csvData.map((item) => ({
            bibNo: item.bibNo,
            participantName: item.participantName,
            ticketName: item.ticketName,
            completionTime: item.completionTime,
            email: item.email,
            phoneNo: item.phoneNumber,
            ageCategory: item.ageCategory || '',
            overallRank: item.overallRank || '',
            genderRank: item.genderRank || '',
            ageRank: item.ageRank || '',
            runType: item.runType || '',
            pageId,
            clientId
        }));

        setLoading(true);
        try {
            const response = await postRequest('EventParticipant/PostEventParticipant', apiData);
            if (response.status === 200 || response.status === 201) {
                NotificationManager.success('Data successfully uploaded.');
                fetchParticipants(pageId);
                setIsModalOpen(false);
            } else {
                NotificationManager.error('Failed to upload data.');
            }
        } catch (error) {
            NotificationManager.error('An error occurred while uploading data.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);

        if (value.trim() === '') {
            setFilteredData(participantData);
        } else {
            setFilteredData(
                participantData.filter((item) =>
                    (item.participantName || '').toLowerCase().includes(value) ||
                    (item.bibNo || '').toLowerCase().includes(value) ||
                    (item.ticketName || '').toLowerCase().includes(value) ||
                    (item.ageCategory || '').toString().toLowerCase().includes(value) ||
                    (item.overallRank || '').toString().toLowerCase().includes(value) ||
                    (item.genderRank || '').toString().toLowerCase().includes(value) ||
                    (item.ageRank || '').toString().toLowerCase().includes(value) ||
                    (item.runType || '').toString().toLowerCase().includes(value)
                )
            );
        }
    };

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const escapeCsvValue = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        const needsQuotes = /[",\n]/.test(stringValue);
        const escaped = stringValue.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
    };

    const handleDownloadExcel = () => {
        const headers = [
            'BIB Number',
            'Ticket Name',
            'Attendee Name',
            'Completion Time',
            'Email',
            'Phone Number',
            'Age Category',
            'Overall Rank',
            'Gender Rank',
            'Age Rank',
            'Run Type'
        ];

        const rows = filteredData.map((item) => [
            item.bibNo,
            item.ticketName,
            item.participantName,
            item.completionTime,
            item.email,
            item.phoneNo,
            item.ageCategory,
            item.overallRank,
            item.genderRank,
            item.ageRank,
            item.runType
        ]);

        const csvContent = [
            headers.map(escapeCsvValue).join(','),
            ...rows.map((row) => row.map(escapeCsvValue).join(','))
        ].join('\r\n');

        const blob = new Blob([`\ufeff${csvContent}`], {
            type: 'text/csv;charset=utf-8;'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${pageName}-participant.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const duplicateBibNos = React.useMemo(() => {
        const counts = {};

        filteredData.forEach(item => {
            if (!item.bibNo) return;
            counts[item.bibNo] = (counts[item.bibNo] || 0) + 1;
        });

        return new Set(
            Object.keys(counts).filter(bibNo => counts[bibNo] > 1)
        );
    }, [filteredData]);


    return (
        <MainA className="b-participants">
            <div className="event-header">
                <div className='event-name'>
                    <h1>Event Participants</h1>
                    <p>{pageName}</p>
                </div>

                <div className="candidate-buttons">
                    <Input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="search-input"
                    />

                    {filteredData.length !== 0 && (
                        <FontAwesomeIcon
                            onClick={handleDownloadExcel}
                            icon={faFileExcel}
                            title='Download Participant Excel'
                            style={{ color: '#1d6f42' }}
                            className='excel-btn'
                        />
                    )}
                    <Button
                        className="modal-open-button"
                        type="button"
                        onClick={openModal}
                    >
                        <img src={plus} alt="plus icon" />
                        Add Participants
                    </Button>


                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(-1);
                        }}
                    >
                        <img src={backArrow} alt='left arrow' />
                        Back
                    </Button>
                </div>
            </div>

            {loading ? (
                <Loader loading={loading} />
            ) : (
                <div className="participants-table">
                    <Table pagination={true} pageSize={10} totalRows={filteredData.length} categoryName="participants">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('bibNo')}>
                                    Bib Number {sortColumn === 'bibNo' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('ticketName')}>
                                    Ticket Name {sortColumn === 'ticketName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('participantName')}>
                                    Participant Name {sortColumn === 'participantName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('phoneNo')}>
                                    Phone No {sortColumn === 'phoneNo' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('email')}>
                                    Email {sortColumn === 'email' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('completionTime')}>
                                    Completion Time {sortColumn === 'completionTime' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('ageCategory')}>
                                    Age Category {sortColumn === 'ageCategory' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('ageRank')}>
                                    Age Rank {sortColumn === 'ageRank' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('genderRank')}>
                                    Gender Rank {sortColumn === 'genderRank' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('overallRank')}>
                                    Overall Rank {sortColumn === 'overallRank' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                <th onClick={() => handleSort('runType')}>
                                    Run Type {sortColumn === 'runType' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                                </th>
                                {/* <th>
                                    Actions
                                </th> */}
                            </tr>
                        </thead>

                        <tbody>
                            {filteredData.length > 0 ? (
                                filteredData.map((item, index) => {
                                    const isDuplicate = duplicateBibNos.has(item.bibNo);

                                    return (
                                        <tr
                                            key={index}
                                            style={{
                                                backgroundColor: isDuplicate ? '#ffe5e5' : '',
                                                color: isDuplicate ? '#d8000c' : ''
                                            }}
                                        >
                                            <td>{item.bibNo}</td>
                                            <td>{item.ticketName}</td>
                                            <td>{item.participantName}</td>
                                            <td>{item.phoneNo}</td>
                                            <td>{item.email}</td>
                                            <td>{item.completionTime}</td>
                                            <td>{item.ageCategory}</td>
                                            <td>{item.ageRank}</td>
                                            <td>{item.genderRank}</td>
                                            <td>{item.overallRank}</td>
                                            <td>{item.runType}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="12" style={{ textAlign: 'center' }}>
                                        No participant(s) found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            )}
            <Modal
                className="csv-upload-modal"
                isOpen={isModalOpen}
                closeModal={closeModal}
            >
                <div className='upload-image'>
                    <img src={upload} alt='uplod image' />
                </div>
                <h2>Upload CSV File</h2>
                <Upload
                    allowedFiles={['csv']}
                    onChange={handleFileUpload}
                    resetFile={false}
                />
                <Button
                    className="upload-button"
                    type="button"
                    onClick={() => uploadCSVData(csvData)}
                    disabled={loading}
                >
                    {loading ? 'Uploading...' : 'Upload'}
                </Button>
                <a
                    className="download-sample-button"
                    onClick={() => {
                        const link = document.createElement('a');
                        link.href = sampleFile;
                        link.download = 'sample-participants.csv';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }}
                >
                    Download Sample CSV
                </a>
            </Modal>
        </MainA>
    );
};

export default Participants;
